/** ProtectPassword/index.tsx - Renders the project password lock overlay. */
import { I_Scene } from '@containers/Home/types';
import React, { memo, useCallback, useMemo, useState } from 'react';
import KrpanoBasic from '@components/Krpano/KrpanoBasic';
import { Button, Input } from 'antd';
import { useSelector } from 'react-redux';
import { selectIsMatterportProject } from '@containers/Home/selectors';
import dynamic from 'next/dynamic';
import { motion } from 'motion/react';
import {
  Provider as ToastProvider,
  Root as ToastRoot,
  Description as ToastDescription,
  Close as ToastClose,
  Viewport as ToastViewport,
} from '@radix-ui/react-toast';

const MatterportContainer = dynamic(
  () => import('@containers/Home/components/MatterportContainer'),
  { ssr: false },
);

const { Password } = Input;
const panelReveal = {
  hidden: { opacity: 0, y: 24, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.36,
      ease: 'easeOut',
      when: 'beforeChildren',
      staggerChildren: 0.12,
      delayChildren: 0.08,
    },
  },
};

const itemReveal = {
  hidden: { opacity: 0, y: 14, filter: 'blur(8px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.34, ease: 'easeOut' },
  },
};

const toastReveal = {
  hidden: { opacity: 0, y: 20, scale: 0.97, filter: 'blur(8px)' },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: 0.28, ease: 'easeOut' },
  },
};

const previewContainerStyle = {
  height: '100%',
  position: 'absolute',
  width: '100%',
  top: 0,
  left: 0,
} as const;

const themePrimaryStyle = {
  background: 'var(--color-primary)',
  color: 'var(--color-text)',
} as const;

interface Props {
  scene?: I_Scene;
  password: string;
  onSubmit: () => void;
}

export const ProtectPassword = memo(({ scene, password, onSubmit }: Props) => {
  const [pw, setPw] = useState('');
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastKey, setToastKey] = useState(0);

  const isMatterportProject = useSelector(selectIsMatterportProject);

  const urlTour = useMemo(
    () =>
      scene?.media?.src_preview || scene?.media?.src_thumb || scene?.media?.src,
    [scene],
  );

  const onOK = useCallback(() => {
    if (pw !== password) {
      setToastKey(prev => prev + 1);
      setIsToastOpen(true);
    } else onSubmit();
  }, [password, pw, onSubmit]);

  return (
    <ToastProvider swipeDirection="down" duration={2800}>
      <div className="relative h-screen w-screen text-white">
        {isMatterportProject ? (
          <MatterportContainer />
        ) : (
          <KrpanoBasic
            urlTour={urlTour || ''}
            scene={scene || undefined}
            krpanoId="previewViewer"
            divId="previewContainer"
            styleContainer={previewContainerStyle}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/25">
          <motion.div
            variants={panelReveal}
            initial="hidden"
            animate="show"
            className="relative flex w-md flex-col items-center justify-center gap-6 rounded-2xl bg-black/20 bg-[linear-gradient(114deg,rgba(255,255,255,0.01),rgba(255,255,255,0.12)_43%,transparent_43.1%,transparent)] p-6 shadow-sm ring-1 ring-white/40 backdrop-blur-sm"
          >
            <motion.div
              variants={itemReveal}
              className="z-1 flex size-12 items-center justify-center rounded-full text-xl bg-white/20"
              style={themePrimaryStyle}
            >
              <i aria-hidden className="fad fa-lock" />
            </motion.div>
            <motion.div
              variants={itemReveal}
              className="z-2 flex w-full flex-col gap-4 text-center"
            >
              <div className="text-lg font-semibold text-white">
                This Project is locked by owner!
              </div>
              <motion.div variants={itemReveal} className="flex w-full gap-3">
                <Password
                  autoFocus
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  placeholder="Please enter password"
                  onPressEnter={onOK}
                  className="!w-full !rounded-full !border !border-white/50 !bg-black/15 !text-white [&_.ant-input]:!bg-transparent [&_.ant-input]:!text-white [&_.ant-input-password-icon]:!text-white [&.ant-input-affix-wrapper-focused]:!border-white [&.ant-input-affix-wrapper-focused]:!shadow-[0_0_0_4px_rgba(255,255,255,0.25)]"
                />
                <Button
                  disabled={!pw}
                  type="primary"
                  onClick={onOK}
                  className="h-auto !rounded-full !border-none px-9 py-2 !text-sm !font-semibold !outline-none"
                  style={themePrimaryStyle}
                >
                  Access
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <ToastRoot
        key={toastKey}
        open={isToastOpen}
        onOpenChange={setIsToastOpen}
        asChild
      >
        <motion.div
          variants={toastReveal}
          initial="hidden"
          animate="show"
          className="group pointer-events-auto w-max max-w-md overflow-hidden rounded-2xl ring-1 ring-white/20 bg-black/30 shadow-[0_24px_50px_rgba(0,0,0,0.35)] backdrop-blur-sm"
        >
          <div className="flex items-center gap-3 p-4">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-500/90 text-lg text-white">
              <i aria-hidden className="fas fa-times" />
            </div>
            <ToastDescription className="text-sm text-white">
              Incorrect password.
            </ToastDescription>
            <ToastClose
              aria-label="Close"
              className="rounded-full shrink-0 size-8 inline-flex items-center justify-center text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
            >
              <i aria-hidden className="fal fa-times" />
            </ToastClose>
          </div>
        </motion.div>
      </ToastRoot>
      <ToastViewport className="pointer-events-none fixed bottom-6 left-1/2 z-[1200] flex w-full -translate-x-1/2 justify-center px-4 outline-none" />
    </ToastProvider>
  );
});

export default ProtectPassword;
